import React, {
    useMemo,
    useEffect,
    useState,
    useCallback,
    useRef,
    useContext,
} from 'react'
import {
    useTable,
    usePagination,
    useSortBy,
    useFilters,
    useExpanded,
    useRowSelect,
    useResizeColumns,
    useBlockLayout,
} from 'react-table'
import {
    FaAngleUp,
    FaAngleDown,
    FaAngleRight,
    FaAngleDoubleLeft,
    FaAngleDoubleRight,
    FaAngleLeft,
    FaSyncAlt,
    FaSearch,
    FaUndoAlt,
} from 'react-icons/fa'
import { StatusColors } from '../../../../utils/StatusColors'
import SubGridData from './SubGridData'
import { DefaultTooltip } from '../../../elem/Tooltip'
import ExportButton from '../../../elem/ExportButton'
import GroupActionSelect from '../Actions/BatchActionSelect'
import ReportSelect from '../Reports/ReportSelect'
import {
    DefaultColumnFilter,
    fuzzyTextFilterFn,
} from '../../../elem/Grid/GridSearch'
import { ParameterContext } from '../../../wrappers/ParameterContext'
import tableColumns from '../../../../utils/gridColumns'
import LoadingSpinner from '../../../elem/LoadingSpinner'

const defaultColumn = () => ({
    Filter: DefaultColumnFilter,
    disableResizing: false,
    minWidth: 100,
    width: 160,
    maxWidth: 400,
})

const filterTypes = () => ({
    fuzzyText: fuzzyTextFilterFn,

    text: (rows, id, filterValue) => {
        return rows.filter((row) => {
            const rowValue = row.values[id]
            return rowValue !== undefined
                ? String(rowValue)
                      .toLowerCase()
                      .startsWith(String(filterValue).toLowerCase())
                : true
        })
    },
})

fuzzyTextFilterFn.autoRemove = (val) => !val

const RowCheckbox = React.forwardRef(({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef()
    const resolvedRef = ref || defaultRef

    React.useEffect(() => {
        resolvedRef.current.indeterminate = indeterminate
    }, [resolvedRef, indeterminate])

    return (
        <>
            <input type="checkbox" ref={resolvedRef} {...rest} />
        </>
    )
})

const ProcessGrid = ({
    filteredData,
    transitions,
    setFilteredData,
    filters,
    actioned,
    setActioned,
    attributeColumns,
    loading,
}) => {
    const [newData, setNewData] = useState(filteredData)
    const [newRow, setNewRow] = useState(null)
    // const [tooltip, setTooltip] = useState(null)
    const [selectedRows, setSelectedRows] = useState([])

    const { gridQuery, clear, setClear } = useContext(ParameterContext)

    const query = useMemo(
        () => (gridQuery.length ? gridQuery.map((x) => x.id) : []),
        [gridQuery]
    )

    const [visibleSearchBox, setVisibleSearchBox] = useState(query)

    const handleSearch = useCallback(
        (column) => {
            const id = column.id
            if (id) {
                visibleSearchBox.includes(id)
                    ? setVisibleSearchBox(
                          visibleSearchBox.filter((x) => x !== id)
                      )
                    : setVisibleSearchBox([...visibleSearchBox, id])
            }
        },
        [visibleSearchBox]
    )

    useEffect(() => {
        if (clear) {
            setVisibleSearchBox([])
            setClear(false)
        }
    }, [clear])

    const dataRef = useRef(null)

    const [lastUpdate, setLastUpdate] = useState(
        new Date().toString().slice(4, 21)
    )
    const [groupActions, setGroupActions] = useState({})

    const updateRow = useCallback(
        (rowIndex, columnId, value) => {
            setFilteredData((old) =>
                old.map((row, index) => {
                    if (index === rowIndex) {
                        return {
                            ...old[rowIndex],
                            [columnId]: value,
                        }
                    }
                    return row
                })
            )
        },
        [setFilteredData]
    )

    useEffect(() => {
        if (selectedRows.length) {
            const possibleTransitions = {
                Ids: selectedRows.map((row) => row.original.ExternalDataItemId),
                Actions: selectedRows
                    .map((row) =>
                        transitions
                            .filter(
                                (transition) =>
                                    transition.CurrentStateId ===
                                    Number(row.original.StateId)
                            )
                            .map((x) => x.ProcessStateTransitionId)
                    )
                    .reduce((acc, curr) => {
                        return acc.filter((item) => curr.includes(item))
                    }),
            }
            setGroupActions(possibleTransitions)
        } else {
            setGroupActions({})
        }
    }, [selectedRows])

    const renderRowSubComponent = useCallback(
        ({ row }) => (
            <pre>
                <SubGridData
                    id={row.original.ExternalDataItemId}
                    setActioned={setActioned}
                />
            </pre>
        ),
        []
    )
    const columns = useMemo(() => tableColumns(attributeColumns), [])
    const tableData = useMemo(() => filteredData, [filteredData])

    const width = useMemo(
        () => ({
            columnWidths: columns.map((x) =>
                x.width ? `${x.width}px` : '60px'
            ),
        }),
        []
    )

    const refreshData = () => {
        dataRef.current = dataRef.current + 1
        setActioned(`ref_${dataRef.current}`)
        setLastUpdate(new Date().toString().slice(4, 21))
    }

    const {
        getTableProps,
        getTableBodyProps,
        toggleAllRowsSelected,
        headerGroups,
        prepareRow,
        visibleColumns,
        page,
        // rows,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        selectedFlatRows,
        // resetResizing,

        state: { pageIndex, pageSize, sortBy, expanded, selectedRowIds },
    } = useTable(
        {
            columns,
            autoResetPage: false,
            data: tableData,
            defaultColumn,
            filterTypes,
            autoResetSelectedRows: false,
            initialState: {
                pageIndex: 0,
                pageSize: 25,
                filters: gridQuery,
                gridLayout: width,
            },
            disableMultiSort: true,
            autoResetFilters: false,
            updateRow,
        },
        useFilters,
        useSortBy,
        useExpanded,
        usePagination,
        useResizeColumns,
        useBlockLayout,
        useRowSelect,
        (hooks) => {
            hooks.visibleColumns.push((columns) => {
                return [
                    {
                        id: 'selection',
                        groupByBoundary: true,
                        width: 35,
                        disableResizing: true,
                        Header: ({ getToggleAllPageRowsSelectedProps }) => (
                            <div style={{ paddingLeft: 0 }}>
                                <RowCheckbox
                                    {...getToggleAllPageRowsSelectedProps()}
                                />
                            </div>
                        ),
                        Cell: ({ row }) => (
                            <div>
                                <RowCheckbox
                                    {...row.getToggleRowSelectedProps()}
                                />
                            </div>
                        ),
                    },
                    ...columns,
                ]
            })
        }
    )

    useEffect(() => {
        setSelectedRows(selectedFlatRows)

        if (actioned !== newRow) {
            toggleAllRowsSelected(false)
            setSelectedRows([])
            setGroupActions({})
            setNewRow(actioned)
            setLastUpdate(new Date().toString().slice(4, 21))
        }
    }, [selectedRowIds, actioned])

    useEffect(() => {
        setNewData(filteredData)
        if (filteredData !== newData) {
            gotoPage(0)
            toggleAllRowsSelected(false)
            setSelectedRows([])
            setGroupActions({})
        }
    }, [filters])

    return (
        <>
            <div className="aboveGrid">
                <GroupActionSelect
                    groupActions={groupActions}
                    transitions={transitions}
                />
                <ReportSelect
                    id={
                        selectedRows.length === 1
                            ? selectedRows[0].original.ExternalDataItemId
                            : null
                    }
                />
            </div>

            <div className="box gridWrapper">
                <div className="controls">
                    <div
                        data-tip={`Refresh Data <br/> <hp className="is-size-7">Last Updated: ${lastUpdate}</hp>`}
                        data-for="refresh"
                        className=" is-size-65"
                        onClick={refreshData}
                    >
                        <FaSyncAlt />

                        <DefaultTooltip id="refresh" place={'top'} />
                    </div>

                    <ExportButton data={filteredData} view={'Filtered'} />
                    <ExportButton data={'All'} view={'All'} />
                </div>
                <div className="gridTop">
            <div className="gridLoading">
                {loading ? (
                            <LoadingSpinner />
                        ) : null}
                </div>
                    <div className="pagination">
                        <span className="outer">
                            Page:{' '}
                            <input
                                type="number"
                                defaultValue={pageIndex + 1}
                                onChange={(e) => {
                                    const page = e.target.value
                                        ? Number(e.target.value) - 1
                                        : 0
                                    gotoPage(page)
                                }}
                                style={{ width: '50.5px' }}
                            />
                        </span>{' '}
                        <div className="pagDiv">
                            <button
                                className="arrow"
                                onClick={() => gotoPage(0)}
                                disabled={!canPreviousPage}
                            >
                                <FaAngleDoubleLeft />
                            </button>{' '}
                            <button
                                className="arrow"
                                onClick={() => previousPage()}
                                disabled={!canPreviousPage}
                            >
                                <FaAngleLeft />
                            </button>{' '}
                            <span>
                                <strong className="is-size-6">
                                    Page {pageIndex + 1} of{' '}
                                    {!pageOptions.length
                                        ? 1
                                        : pageOptions.length}
                                </strong>{' '}
                            </span>
                            <button
                                className="arrow"
                                onClick={() => nextPage()}
                                disabled={!canNextPage}
                            >
                                <FaAngleRight />
                            </button>{' '}
                            <button
                                className="arrow"
                                onClick={() => gotoPage(pageCount - 1)}
                                disabled={!canNextPage}
                            >
                                <FaAngleDoubleRight />
                            </button>{' '}
                        </div>
                        <div className="pagDiv outer">
                            <select
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value))
                                }}
                            >
                                {[25, 50, 100, 500, 1000].map((pageSize) => (
                                    <option key={pageSize} value={pageSize}>
                                        Show {pageSize}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
                <div className="showing">
                    <div style={{ marginLeft: '1%' }}>
                        {!filteredData.length
                            ? `Loading Grid Data`
                            : `Showing ${page.length} of ${filteredData.length} results`}
                    </div>
                </div>

                <div
                    {...getTableProps()}
                    className="table is-hoverable resizable"
                >
                    <div>
                        {headerGroups.map((headerGroup) => (
                            <div
                                {...headerGroup.getHeaderGroupProps({
                                    // style: { paddingRight: '15px' },
                                })}
                                className="tr"
                            >
                                {headerGroup.headers.map((column) => (
                                    <div
                                        {...column.getHeaderProps()}
                                        className="th"
                                    >
                                        <div className="header">
                                            {column.canFilter ? (
                                                <div
                                                    className="is-size-7 searchIcon"
                                                    onClick={() =>
                                                        handleSearch(column)
                                                    }
                                                >
                                                    <FaSearch />
                                                </div>
                                            ) : null}
                                            <span
                                                {...column.getSortByToggleProps()}
                                            >
                                                {column.render('Header')}
                                                {column.isSorted ? (
                                                    column.isSortedDesc ? (
                                                        <FaAngleUp />
                                                    ) : (
                                                        <FaAngleDown />
                                                    )
                                                ) : (
                                                    ''
                                                )}
                                            </span>
                                        </div>
                                        {column.canResize && (
                                            <div
                                                {...column.getResizerProps()}
                                                className={`resizer ${
                                                    column.isResizing
                                                        ? 'isResizing'
                                                        : ''
                                                }`}
                                            />
                                        )}
                                        <div
                                            style={
                                                visibleSearchBox.length &&
                                                visibleSearchBox.indexOf(
                                                    column.id
                                                ) > -1
                                                    ? null
                                                    : { display: 'none' }
                                            }
                                        >
                                            {column.canFilter
                                                ? column.render('Filter')
                                                : null}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                    <div className="tbody">
                        {page.map((row) => {
                            prepareRow(row)
                            const status = row.original.Status
                            return (
                                <React.Fragment>
                                    <div {...row.getRowProps()} className="tr">
                                        {row.cells.map((cell) => {
                                            const td = cell.getCellProps()
                                            return (
                                                <div
                                                    {...td}
                                                    style={{
                                                        ...td.style,
                                                        color:
                                                            StatusColors[
                                                                status
                                                            ],
                                                        position: 'relative',
                                                    }}
                                                    className="td"
                                                >
                                                    {cell.render('Cell')}
                                                </div>
                                            )
                                        })}
                                    </div>
                                    {row.isExpanded ? (
                                        <div className="tr">
                                            <div
                                                className="td"
                                                colSpan={visibleColumns.length}
                                                style={{ position: 'relative' }}
                                            >
                                                {renderRowSubComponent({ row })}
                                            </div>
                                        </div>
                                    ) : null}
                                </React.Fragment>
                            )
                        })}
                    </div>
                </div>
            </div>
        </>
    )
}

export default ProcessGrid
